import React, { createContext, useContext, useState, useEffect, useRef  } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col } from "antd";
import {
  FetchSmartCubePageFilterAction,
  FetchSmartCubeChartFiltersAction,
  FetchSmartCubeMarketSizeForecastAction,
  FetchSmartCubeCategoryRiskAction,
  FetchSmartCubeTextWidgetsAction,
  FetchSmartCubePriceTrendAction,
  FetchSmartCubeCostDriversAction,
  FetchSmartCubeCostStructureBreakdownAction
} from "../../../redux/action/SmartCubeAction";
import {
  changeFromDateOptionsUtil,
  changeToDateOptionsUtil,
  getDropdownOptions
} from "../../KPIOverview/KpiUtils";
import { useToast } from "../../../components/Toast/ToastContext";
import Vector from "../../../assets/Vector.png";
import SingleSelectDropdown from "../../../components/Dropdown/SingleSelectDropdown";
import { FetchSPGlobalPageLabelCountAction } from "../../../redux/action/SPGlobalAction";

// This context/provider owns the SmartCube "filters" state (Sector,
// Category, From Date, To Date), redux selectors and handler functions,
// mirroring the CMA/Woodmac/S&P Global source pattern so that SmartCube
// logic is fully self-contained and can be shared with <SmartCubeSource/>
// without lifting state back up to the parent (SBACCIScreen.js).
const SmartCubeSourceContext = createContext(null);

export const useSmartCubeSource = () => useContext(SmartCubeSourceContext);

/* istanbul ignore next */
// function ensureFourDigitYear(label) {
//   if (!label) return label;
//   return label.replace(/(\w{3,})\s(\d{2})$/, (m, month, year) => `${month} 20${year}`);
// }
/* istanbul ignore next */
function ensureFourDigitYear(label) {
  if (!label) return label;
  const trimmedLabel = label.trim();
  const lastSpaceIndex = trimmedLabel.lastIndexOf(" ");

  if (lastSpaceIndex === -1) {
      return label;
  }

  const month = trimmedLabel.slice(0, lastSpaceIndex);
  const year = trimmedLabel.slice(lastSpaceIndex + 1);

  if (year.length === 2 && /^\d{2}$/.test(year)) {
      return `${month} 20${year}`;
  }

  return label;
}

/* istanbul ignore next */
function monthLabelToObject(label) {
  if (!label) return null;
  const d = new Date(`${label} 01`);
  if (Number.isNaN(d.getTime())) return null;
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const paddedMonth = String(month).padStart(2, "0");
  return {
    displayText: label,
    id: `${year}-${month}`,
    label,
    value: `${year}-${paddedMonth}-01`
  };
}

/* istanbul ignore next */
function generateMonthObjectsFromLabels(minLabel, maxLabel) {
  const result = [];
  if (!minLabel || !maxLabel) return result;
  const start = new Date(`${minLabel} 01`);
  const end = new Date(`${maxLabel} 01`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return result;
  const monthsDiff =
    (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  if (monthsDiff < 0) return result;
  const formatter = new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" });
  for (let i = 0; i <= monthsDiff; i++) {
    const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const displayText = formatter.format(d);
    const paddedMonth = String(month).padStart(2, "0");
    result.push({ id: `${year}-${month}`, displayText, label: displayText, value: `${year}-${paddedMonth}-01` });
  }
  return result;
}

// Categories in the API response are plain strings scoped under each
// sector (unlike the {name, ...} shaped children used by Woodmac/S&P
// Global), so they need their own small mapping helper before being handed
// off to getDropdownOptions.
function getCategoriesBySector(sectorData, selectedSectorName) {
  if (!Array.isArray(sectorData) || !selectedSectorName) return [];
  const selectedSector = sectorData.find(item => item.name === selectedSectorName);
  if (!selectedSector || !Array.isArray(selectedSector.categories)) return [];
  const sortedCategories = [...new Set(selectedSector.categories)].sort((a, b) => a.localeCompare(b));
  return getDropdownOptions(sortedCategories);
}

// commodity-prices-filter entries are {name: <country>, commodities: [...]}
// - the commodities dropdown is scoped to whichever country is selected, in
// the same order the API returned them (no re-sorting, per requirement that
// the first value returned is auto-selected).
function getCommoditiesByCountry(commodityPricesFilter, selectedCountryName) {
  if (!Array.isArray(commodityPricesFilter) || !selectedCountryName) return [];
  const selectedCountry = commodityPricesFilter.find(item => item.name === selectedCountryName);
  if (!selectedCountry || !Array.isArray(selectedCountry.commodities)) return [];
  return getDropdownOptions(selectedCountry.commodities);
}export const SmartCubeSourceProvider = ({ children, isUserDataFetched, isSmartCubeSelected, l1Category, l2Category }) => {
  const dispatch = useDispatch();
  const [showSmartCubeDetailsModal, setShowSmartCubeDetailsModal] = useState(false);

  const smartCubeFilterData = useSelector(state => state.getSmartCubePageFilterReducer);
  const isSmartCubeFilterDataLoading = smartCubeFilterData?.loading ?? true;

  const smartCubeChartFiltersData = useSelector(state => state.getSmartCubeChartFiltersReducer);

  const smartCubeMarketSizeForecastData = useSelector(state => state.getSmartCubeMarketSizeForecastReducer);

  const smartCubeCategoryRiskData = useSelector(state => state.getSmartCubeCategoryRiskReducer);

  const smartCubeTextWidgetsData = useSelector(state => state.getSmartCubeTextWidgetsReducer);

  const smartCubePriceTrendData = useSelector(state => state.getSmartCubePriceTrendReducer);

  const smartCubeCostDriversData = useSelector(state => state.getSmartCubeCostDriversReducer);

  const smartCubeCostStructureBreakdownData = useSelector(state => state.getSmartCubeCostStructureBreakdownReducer);

  const userData = useSelector(state => state.getUserInfoReducer);
  const isUserDataLoading = userData?.loading ?? true;

  // isSmartCubeFilterDataEmpty tracks the case where smartCubeFilterData
  // succeeded but returned no usable sector/dateInfo (months.length === 0),
  // which means loadChartsFilter() (and therefore loadDetailCharts() -
  // Market Size Forecast, Category Risk, Text Widgets, Price Trend, Cost
  // Drivers, Cost Structure Breakdown) never fires. Without this flag, all
  // of those loading states stay undefined forever and the `?? true`
  // fallback leaves the loader stuck on indefinitely instead of falling
  // through to <NoData/>.
  const [isSmartCubeFilterDataEmpty, setIsSmartCubeFilterDataEmpty] = useState(false);

  const isSmartCubeChartFiltersLoading = isSmartCubeFilterDataEmpty ? false : (smartCubeChartFiltersData?.loading ?? true);
  const isSmartCubeMarketSizeForecastLoading = isSmartCubeFilterDataEmpty ? false : (smartCubeMarketSizeForecastData?.loading ?? true);
  const isSmartCubeCategoryRiskLoading = isSmartCubeFilterDataEmpty ? false : (smartCubeCategoryRiskData?.loading ?? true);
  const isSmartCubeTextWidgetsLoading = isSmartCubeFilterDataEmpty ? false : (smartCubeTextWidgetsData?.loading ?? true);
  const isSmartCubePriceTrendLoading = isSmartCubeFilterDataEmpty ? false : (smartCubePriceTrendData?.loading ?? true);
  const isSmartCubeCostDriversLoading = isSmartCubeFilterDataEmpty ? false : (smartCubeCostDriversData?.loading ?? true);
  const isSmartCubeCostStructureBreakdownLoading = isSmartCubeFilterDataEmpty ? false : (smartCubeCostStructureBreakdownData?.loading ?? true);

  const [sectorList, setSectorList] = useState([]);
  const [sectorSelected, setSectorSelected] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [categorySelected, setCategorySelected] = useState([]);

  const [entireSmartCubeMonthList, setEntireSmartCubeMonthList] = useState([]);
  const [smartCubeFromDates, setSmartCubeFromDates] = useState([]);
  const [smartCubeToDates, setSmartCubeToDates] = useState([]);
  const [smartCubeFromDateSelected, setSmartCubeFromDateSelected] = useState([]);
  const [smartCubeToDateSelected, setSmartCubeToDateSelected] = useState([]);

  // Price Trend chart filters (Country -> Commodity parent-child hierarchy)
  const [countryList, setCountryList] = useState([]);
  const [countrySelected, setCountrySelected] = useState([]);
  const [commodityList, setCommodityList] = useState([]);
  const [commoditySelected, setCommoditySelected] = useState([]);

  // Cost Drivers chart filter
  const [costDriverList, setCostDriverList] = useState([]);
  const [costDriverSelected, setCostDriverSelected] = useState([]);

  // Cost Structure Breakdown chart filter
  const [costStructureRegionList, setCostStructureRegionList] = useState([]);
  const [costStructureRegionSelected, setCostStructureRegionSelected] = useState([]);

  // Market Size Forecast chart data
  const [marketSizeForecastChartData, setMarketSizeForecastChartData] = useState([]);
  const [marketSizeForecastUnit, setMarketSizeForecastUnit] = useState(null);
  const [marketSizeForecastCagr, setMarketSizeForecastCagr] = useState(null);
  const [marketSizeForecastLastUpdated, setMarketSizeForecastLastUpdated] = useState(null);
  const [marketSizeForecastNextUpdate, setMarketSizeForecastNextUpdate] = useState(null);

  // Category Risk chart data
  const [categoryRiskChartData, setCategoryRiskChartData] = useState([]);
  const [categoryRiskAvg, setCategoryRiskAvg] = useState(null);
  const [categoryRiskLastUpdated, setCategoryRiskLastUpdated] = useState(null);
  const [categoryRiskNextUpdate, setCategoryRiskNextUpdate] = useState(null);

  // Text widget (Opportunities / Risks / Mega Trends / Sustainability Trends) data
  const [opportunitiesData, setOpportunitiesData] = useState([]);
  const [opportunitiesLastUpdated, setOpportunitiesLastUpdated] = useState(null);
  const [opportunitiesNextUpdate, setOpportunitiesNextUpdate] = useState(null);
  const [risksData, setRisksData] = useState([]);
  const [risksLastUpdated, setRisksLastUpdated] = useState(null);
  const [risksNextUpdate, setRisksNextUpdate] = useState(null);
  const [megaTrendsData, setMegaTrendsData] = useState([]);
  const [megaTrendsLastUpdated, setMegaTrendsLastUpdated] = useState(null);
  const [megaTrendsNextUpdate, setMegaTrendsNextUpdate] = useState(null);
  const [sustainabilityTrendsData, setSustainabilityTrendsData] = useState([]);
  const [sustainabilityTrendsLastUpdated, setSustainabilityTrendsLastUpdated] = useState(null);
  const [sustainabilityTrendsNextUpdate, setSustainabilityTrendsNextUpdate] = useState(null);

  // Price Trend chart data
  const [priceTrendChartData, setPriceTrendChartData] = useState([]);
  const [priceTrendUnit, setPriceTrendUnit] = useState(null);
  const [priceTrendLastUpdated, setPriceTrendLastUpdated] = useState(null);
  const [priceTrendNextUpdate, setPriceTrendNextUpdate] = useState(null);

  // Cost Drivers chart data
  const [costDriversChartData, setCostDriversChartData] = useState([]);
  const [costDriversLastUpdated, setCostDriversLastUpdated] = useState(null);
  const [costDriversNextUpdate, setCostDriversNextUpdate] = useState(null);

  // Cost Structure Breakdown chart data
  const [costStructureBreakdownData, setCostStructureBreakdownData] = useState([]);
  const [costStructureBreakdownLastUpdated, setCostStructureBreakdownLastUpdated] = useState(null);
  const [costStructureBreakdownNextUpdate, setCostStructureBreakdownNextUpdate] = useState(null);

  
  const { showToast } = useToast();
  const linechartRef = useRef();
  const donutchartRef = useRef();
  const costDriversRef = useRef();
  const marketSizeRef = useRef();
  const categoryRiskRef = useRef();
  
  const loadChartsFilter = () => {
    
    const sectorVal = JSON.parse(localStorage.getItem("sc-sector") || "[]");
    const categoryVal = JSON.parse(localStorage.getItem("sc-category") || "[]");

    
    const sector = sectorVal?.[0]?.displayText;
    const category = categoryVal?.[0]?.displayText;
    if (isUserDataFetched && isSmartCubeSelected && sector && category) {
      dispatch(FetchSmartCubeChartFiltersAction({ sector, category }));
    }
  }

  const loadDetailCharts = (chart=null) => {
    const categoryVal = JSON.parse(localStorage.getItem("sc-category") || "[]");
    const sectorVal = JSON.parse(localStorage.getItem("sc-sector") || "[]");
    const category = categoryVal?.[0]?.displayText;
    const sector = sectorVal?.[0]?.displayText;
    
    const fromDate = JSON.parse(localStorage.getItem("sc-fromDate") || "[]")?.[0]?.value;
    const toDate = JSON.parse(localStorage.getItem("sc-toDate") || "[]")?.[0]?.value;

    const country = JSON.parse(localStorage.getItem("sc-country") || "[]")?.[0]?.displayText;
    const commodity = JSON.parse(localStorage.getItem("sc-commodity") || "[]")?.[0]?.displayText;
    const region = JSON.parse(localStorage.getItem("sc-cost-structure-region") || "[]")?.[0]?.displayText;
    const costDriver = JSON.parse(localStorage.getItem("sc-cost-driver") || "[]")?.[0]?.displayText;

    if(chart===1) {
      
      dispatch(FetchSmartCubePriceTrendAction({ sector, category, country, commodity, fromDate, toDate }));

    } else if(chart===2) {

      dispatch(FetchSmartCubeCostStructureBreakdownAction({ sector, category, region }));
    } else if(chart===3) {

      dispatch(FetchSmartCubeCostDriversAction({ sector, category, costDriver, fromDate, toDate }));
    }  else {
      dispatch(FetchSmartCubeMarketSizeForecastAction({ sector, category }));
      dispatch(FetchSmartCubeCategoryRiskAction({ sector, category }));
      dispatch(FetchSmartCubeTextWidgetsAction({ sector, category }));
    
      dispatch(FetchSmartCubePriceTrendAction({ sector, category, country, commodity, fromDate, toDate }));
      dispatch(FetchSmartCubeCostDriversAction({ sector, category, costDriver, fromDate, toDate }));
      dispatch(FetchSmartCubeCostStructureBreakdownAction({ sector, category, region }));
    }


  }
  useEffect(() => {
    if (isUserDataFetched && smartCubeChartFiltersData?.type === "SUCCESS_SMARTCUBE_CHART_FILTERS_ACTION") {
      const commodityPricesFilter = smartCubeChartFiltersData?.["commodity-prices-filter"];
      const countries = getDropdownOptions((commodityPricesFilter || []).map(item => item.name));
      setCountryList(countries);
      const defaultCountry = countries?.[0] ? [countries[0]] : [];
      setCountrySelected(defaultCountry);

      const commodities = getCommoditiesByCountry(commodityPricesFilter, defaultCountry?.[0]?.displayText);
      setCommodityList(commodities);
      setCommoditySelected(commodities?.[0] ? [commodities[0]] : []);

      const costDrivers = getDropdownOptions(smartCubeChartFiltersData?.["cost-drivers-filter"]);
      setCostDriverList(costDrivers);
      setCostDriverSelected(costDrivers?.[0] ? [costDrivers[0]] : []);

      const costStructureRegions = getDropdownOptions(smartCubeChartFiltersData?.["cost-structure-filter"]);
      setCostStructureRegionList(costStructureRegions);
      setCostStructureRegionSelected(costStructureRegions?.[0] ? [costStructureRegions[0]] : []);

      // Once the chart-filters dropdowns have all been resolved to their
      // default (first) values, fetch the chart-content APIs for the same
      // sector/category - Market Size Forecast, Category Risk and the
      // Opportunities/Risks/Mega Trends/Sustainability Trends text widgets.
      // const sector = sectorSelected?.[0]?.displayText;
      // const category = categorySelected?.[0]?.displayText;
      // if (sector && category) {
      //   dispatch(FetchSmartCubeMarketSizeForecastAction({ sector, category }));
      //   dispatch(FetchSmartCubeCategoryRiskAction({ sector, category }));
      //   dispatch(FetchSmartCubeTextWidgetsAction({ sector, category }));
      // }

      localStorage.setItem("sc-country", JSON.stringify(defaultCountry));
      localStorage.setItem("sc-commodity", JSON.stringify(commodities?.[0] ? [commodities[0]] : []));
      localStorage.setItem("sc-cost-driver", JSON.stringify(costDrivers?.[0] ? [costDrivers[0]] : []));
      localStorage.setItem("sc-cost-structure-region", JSON.stringify(costStructureRegions?.[0] ? [costStructureRegions[0]] : []));

      loadDetailCharts();

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartCubeChartFiltersData, isUserDataFetched]);

  const onSectorChange = val => {
    if (val && val.length > 0) {
      setSectorSelected(val);

      const categories = getCategoriesBySector(smartCubeFilterData?.sector, val[0]?.displayText);
      setCategoryList(categories);
      setCategorySelected(categories?.[0] ? [categories[0]] : []);

      
      localStorage.setItem("sc-sector", JSON.stringify(val));
      localStorage.setItem("sc-category", JSON.stringify(categories?.[0] ? [categories[0]] : []));
      loadChartsFilter();


    }
  };



  const onCategoryChange = val => {
    if (val && val.length > 0) {
      setCategorySelected(val);
      localStorage.setItem("sc-category", JSON.stringify(val));
      loadChartsFilter();
    }
  };

  const onSmartCubeFromDateChange = val => {
    if (val && val.length > 0) {
      setSmartCubeFromDateSelected(val);
      setSmartCubeToDates(changeToDateOptionsUtil(val[0], entireSmartCubeMonthList));
      localStorage.setItem("sc-fromDate", JSON.stringify(val));
      loadDetailCharts();
    }
  };

  const onSmartCubeToDateChange = val => {
    if (val && val.length > 0) {
      setSmartCubeToDateSelected(val);
      setSmartCubeFromDates(changeFromDateOptionsUtil(val[0], entireSmartCubeMonthList));
      localStorage.setItem("sc-toDate", JSON.stringify(val));
      loadDetailCharts();
    }
  };

  const onCountryChange = val => {
    if (val && val.length > 0) {
      setCountrySelected(val);

      const commodities = getCommoditiesByCountry(smartCubeChartFiltersData?.["commodity-prices-filter"], val[0]?.displayText);
      setCommodityList(commodities);
      setCommoditySelected(commodities?.[0] ? [commodities[0]] : []);
      localStorage.setItem("sc-country", JSON.stringify(val));
      localStorage.setItem("sc-commodity", JSON.stringify(commodities?.[0] ? [commodities[0]] : []));
      loadDetailCharts(1);
    }
  };

  const onCommodityChange = val => {
    if (val && val.length > 0) {
      setCommoditySelected(val);
      localStorage.setItem("sc-commodity", JSON.stringify(val));
      loadDetailCharts(1);
    }
  };

  const onCostDriverChange = val => {
    if (val && val.length > 0) {
      setCostDriverSelected(val);
      localStorage.setItem("sc-cost-driver", JSON.stringify(val));
      loadDetailCharts(3);
    }
  };

  const onCostStructureRegionChange = val => {
    if (val && val.length > 0) {
      setCostStructureRegionSelected(val);
      localStorage.setItem("sc-cost-structure-region", JSON.stringify(val));
      loadDetailCharts(2);
    }
  };

  const filterLabelStyle = {
    fontFamily: "Inter",
    fontSize: "0.75rem",
    fontWeight: "400",
    marginBottom: "3px",
    color: "#3A3A3A"
  };

  const renderSmartCubeFilterDropdown = (label, childList, selectedValue, setSelectedValue, colProps = {}) => (
    <Col {...colProps}>
      <div style={filterLabelStyle}>{label}</div>
      <SingleSelectDropdown
        className="buy-con-dropdown-modal cma-dropdown risi-dropdown"
        childList={childList}
        selectedValue={selectedValue}
        setSelectedValue={setSelectedValue}
      />
    </Col>
  );

  // Fetch SmartCube filter data whenever SmartCube becomes the active
  // source, once user info has been fetched, for the current L1/L2 category
  // (derived from miL2Data.l1Category and the matching cardDetail's name in
  // SBACCIScreen.js, mirroring how SPGlobalSourceProvider is fed category/
  // subcategory).
  useEffect(() => {
    if (isUserDataFetched && isSmartCubeSelected && l1Category && l2Category) {
      dispatch(FetchSmartCubePageFilterAction(l1Category, l2Category));
      const location = window.location.pathname;
      dispatch(FetchSPGlobalPageLabelCountAction(location));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserDataFetched, isSmartCubeSelected, l1Category, l2Category]);

  useEffect(() => {
    if (isUserDataFetched && smartCubeFilterData?.type === "SUCCESS_SMARTCUBE_PAGE_FILTER_ACTION") {
      const sortedSectorNames = [...new Set((smartCubeFilterData?.sector || []).map(s => s.name))].sort((a, b) => a.localeCompare(b));
      const sectors = getDropdownOptions(sortedSectorNames);
      setSectorList(sectors);
      const defaultSector = sectors?.[0] ? [sectors[0]] : [];
      setSectorSelected(defaultSector);

      const categories = getCategoriesBySector(smartCubeFilterData?.sector, defaultSector?.[0]?.displayText);
      setCategoryList(categories);
      setCategorySelected(categories?.[0] ? [categories[0]] : []);

      const { minYearMonth, maxYearMonth, maxDate } = smartCubeFilterData?.dateInfo || {};
      const months = generateMonthObjectsFromLabels(ensureFourDigitYear(minYearMonth), ensureFourDigitYear(maxYearMonth));
      if (months.length) {
        setIsSmartCubeFilterDataEmpty(false);
        setEntireSmartCubeMonthList(months);

        const defaultTo = months[months.length - 1];
        const maxDateObj = maxDate ? new Date(maxDate) : null;
        const defaultFromLabel = maxDateObj && !Number.isNaN(maxDateObj.getTime())
          ? new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" }).format(
              new Date(maxDateObj.getFullYear() - 2, maxDateObj.getMonth(), 1)
            )
          : null;
        const defaultFrom = (defaultFromLabel && months.find(m => m.displayText === defaultFromLabel))
          || monthLabelToObject(defaultFromLabel)
          || months[0];

        setSmartCubeFromDates(changeFromDateOptionsUtil(defaultTo, months));
        setSmartCubeToDates(changeToDateOptionsUtil(defaultFrom, months));
        setSmartCubeFromDateSelected([defaultFrom]);
        setSmartCubeToDateSelected([defaultTo]);

        localStorage.setItem("sc-sector", JSON.stringify(defaultSector));
        localStorage.setItem("sc-category", JSON.stringify(categories?.[0] ? [categories[0]] : []));
        localStorage.setItem("sc-fromDate", JSON.stringify([defaultFrom]));
        localStorage.setItem("sc-toDate", JSON.stringify([defaultTo]));

        loadChartsFilter();
      } else {
        setIsSmartCubeFilterDataEmpty(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartCubeFilterData, isUserDataFetched]);

  // Whenever the selected Category changes - whether the user picked it
  // directly, or it was auto-updated as a result of a Sector change - fetch
  // the chart-level filter options (Price Trend's Country/Commodity, Cost
  // Drivers, Cost Structure Breakdown's Region) for that sector/category.
  // useEffect(() => {
  //   const sector = sectorSelected?.[0]?.displayText;
  //   const category = categorySelected?.[0]?.displayText;
  //   if (isUserDataFetched && isSmartCubeSelected && sector && category) {
  //     dispatch(FetchSmartCubeChartFiltersAction({ sector, category }));
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isUserDataFetched, isSmartCubeSelected, sectorSelected, categorySelected]);

  // useEffect(() => {
  //   if (isUserDataFetched && smartCubeChartFiltersData?.type === "SUCCESS_SMARTCUBE_CHART_FILTERS_ACTION") {
  //     const commodityPricesFilter = smartCubeChartFiltersData?.["commodity-prices-filter"];
  //     const countries = getDropdownOptions((commodityPricesFilter || []).map(item => item.name));
  //     setCountryList(countries);
  //     const defaultCountry = countries?.[0] ? [countries[0]] : [];
  //     setCountrySelected(defaultCountry);

  //     const commodities = getCommoditiesByCountry(commodityPricesFilter, defaultCountry?.[0]?.displayText);
  //     setCommodityList(commodities);
  //     setCommoditySelected(commodities?.[0] ? [commodities[0]] : []);

  //     const costDrivers = getDropdownOptions(smartCubeChartFiltersData?.["cost-drivers-filter"]);
  //     setCostDriverList(costDrivers);
  //     setCostDriverSelected(costDrivers?.[0] ? [costDrivers[0]] : []);

  //     const costStructureRegions = getDropdownOptions(smartCubeChartFiltersData?.["cost-structure-filter"]);
  //     setCostStructureRegionList(costStructureRegions);
  //     setCostStructureRegionSelected(costStructureRegions?.[0] ? [costStructureRegions[0]] : []);

  //     // Once the chart-filters dropdowns have all been resolved to their
  //     // default (first) values, fetch the chart-content APIs for the same
  //     // sector/category - Market Size Forecast, Category Risk and the
  //     // Opportunities/Risks/Mega Trends/Sustainability Trends text widgets.
  //     const sector = sectorSelected?.[0]?.displayText;
  //     const category = categorySelected?.[0]?.displayText;
  //     if (sector && category) {
  //       dispatch(FetchSmartCubeMarketSizeForecastAction({ sector, category }));
  //       dispatch(FetchSmartCubeCategoryRiskAction({ sector, category }));
  //       dispatch(FetchSmartCubeTextWidgetsAction({ sector, category }));
  //     }
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [smartCubeChartFiltersData, isUserDataFetched]);

  // TO KEEP
  useEffect(() => {
    if (isUserDataFetched && smartCubeMarketSizeForecastData?.type === "SUCCESS_SMARTCUBE_MARKET_SIZE_FORECAST_ACTION") {
      setMarketSizeForecastChartData(smartCubeMarketSizeForecastData?.data || []);
      setMarketSizeForecastUnit(smartCubeMarketSizeForecastData?.unit || null);
      setMarketSizeForecastCagr(smartCubeMarketSizeForecastData?.cagr || null);
      setMarketSizeForecastLastUpdated(smartCubeMarketSizeForecastData?.lastUpdated || null);
      setMarketSizeForecastNextUpdate(smartCubeMarketSizeForecastData?.nextUpdate || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartCubeMarketSizeForecastData, isUserDataFetched]);

  // TO KEEP
  useEffect(() => {
    if (isUserDataFetched && smartCubeCategoryRiskData?.type === "SUCCESS_SMARTCUBE_CATEGORY_RISK_ACTION") {
      const mappedData = (smartCubeCategoryRiskData?.data || []).map(item => ({ subject: item.name, risk: item.value }));
      setCategoryRiskChartData(mappedData);
      setCategoryRiskAvg(smartCubeCategoryRiskData?.avg ?? null);
      setCategoryRiskLastUpdated(smartCubeCategoryRiskData?.lastUpdated || null);
      setCategoryRiskNextUpdate(smartCubeCategoryRiskData?.nextUpdate || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartCubeCategoryRiskData, isUserDataFetched]);

  // TO KEEP
  useEffect(() => {
    if (isUserDataFetched && smartCubeTextWidgetsData?.type === "SUCCESS_SMARTCUBE_TEXT_WIDGETS_ACTION") {
      const opportunities = smartCubeTextWidgetsData?.opportunities;
      setOpportunitiesData((opportunities?.data || []).map(item => ({
        title: item.factor,
        description: [
          { label: "Detail", text: item.detail },
          { label: "Implication", text: item.implication }
        ]
      })));
      setOpportunitiesLastUpdated(opportunities?.lastUpdated || null);
      setOpportunitiesNextUpdate(opportunities?.nextUpdate || null);

      const risks = smartCubeTextWidgetsData?.risks;
      setRisksData((risks?.data || []).map(item => ({
        title: item.factor,
        description: [
          { label: "Detail", text: item.detail },
          { label: "Mitigation Strategy", text: item.mitigation_strategy }
        ]
      })));
      setRisksLastUpdated(risks?.lastUpdated || null);
      setRisksNextUpdate(risks?.nextUpdate || null);

      const megatrends = smartCubeTextWidgetsData?.megatrends;
      setMegaTrendsData((megatrends?.data || []).map(item => ({ title: item.insight })));
      setMegaTrendsLastUpdated(megatrends?.lastUpdated || null);
      setMegaTrendsNextUpdate(megatrends?.nextUpdate || null);

      const sustainabilitytrends = smartCubeTextWidgetsData?.sustainabilitytrends;
      setSustainabilityTrendsData((sustainabilitytrends?.data || []).map(item => ({ title: item.insight })));
      setSustainabilityTrendsLastUpdated(sustainabilitytrends?.lastUpdated || null);
      setSustainabilityTrendsNextUpdate(sustainabilitytrends?.nextUpdate || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartCubeTextWidgetsData, isUserDataFetched]);

  // Fetch the Price Trend chart whenever its Country/Commodity selections
  // or the page-level From/To Date range change, once a Sector/Category is
  // also resolved.
  // useEffect(() => {
  //   const sector = sectorSelected?.[0]?.displayText;
  //   const category = categorySelected?.[0]?.displayText;
  //   const country = countrySelected?.[0]?.displayText;
  //   const commodity = commoditySelected?.[0]?.displayText;
  //   const fromDate = smartCubeFromDateSelected?.[0]?.value;
  //   const toDate = smartCubeToDateSelected?.[0]?.value;
  //   if (isUserDataFetched && isSmartCubeSelected && sector && category && country && commodity && fromDate && toDate) {
  //     dispatch(FetchSmartCubePriceTrendAction({ sector, category, country, commodity, fromDate, toDate }));
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isUserDataFetched, isSmartCubeSelected, sectorSelected, categorySelected, countrySelected, commoditySelected, smartCubeFromDateSelected, smartCubeToDateSelected]);

  // TO KEEP
  useEffect(() => {
    if (isUserDataFetched && smartCubePriceTrendData?.type === "SUCCESS_SMARTCUBE_PRICE_TREND_ACTION") {
      setPriceTrendChartData(smartCubePriceTrendData?.data || []);
      setPriceTrendUnit(smartCubePriceTrendData?.unit || null);
      setPriceTrendLastUpdated(smartCubePriceTrendData?.lastUpdated || null);
      setPriceTrendNextUpdate(smartCubePriceTrendData?.nextUpdate || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartCubePriceTrendData, isUserDataFetched]);

  // Fetch the Cost Drivers chart whenever its Cost Driver selection or the
  // page-level From/To Date range change, once a Sector/Category is also
  // resolved.
  // useEffect(() => {
  //   const sector = sectorSelected?.[0]?.displayText;
  //   const category = categorySelected?.[0]?.displayText;
  //   const costDriver = costDriverSelected?.[0]?.displayText;
  //   const fromDate = smartCubeFromDateSelected?.[0]?.value;
  //   const toDate = smartCubeToDateSelected?.[0]?.value;
  //   if (isUserDataFetched && isSmartCubeSelected && sector && category && costDriver && fromDate && toDate) {
  //     dispatch(FetchSmartCubeCostDriversAction({ sector, category, costDriver, fromDate, toDate }));
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isUserDataFetched, isSmartCubeSelected, sectorSelected, categorySelected, costDriverSelected, smartCubeFromDateSelected, smartCubeToDateSelected]);

  // TO KEEP
  useEffect(() => {
    if (isUserDataFetched && smartCubeCostDriversData?.type === "SUCCESS_SMARTCUBE_COST_DRIVERS_ACTION") {
      setCostDriversChartData(smartCubeCostDriversData?.data || []);
      setCostDriversLastUpdated(smartCubeCostDriversData?.lastUpdated || null);
      setCostDriversNextUpdate(smartCubeCostDriversData?.nextUpdate || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartCubeCostDriversData, isUserDataFetched]);

  // Fetch the Cost Structure Breakdown chart whenever its Region selection
  // changes, once a Sector/Category is also resolved. Unlike Price Trend
  // and Cost Drivers, this chart is not date-ranged.
  // useEffect(() => {
  //   const sector = sectorSelected?.[0]?.displayText;
  //   const category = categorySelected?.[0]?.displayText;
  //   const region = costStructureRegionSelected?.[0]?.displayText;
  //   if (isUserDataFetched && isSmartCubeSelected && sector && category && region) {
  //     dispatch(FetchSmartCubeCostStructureBreakdownAction({ sector, category, region }));
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isUserDataFetched, isSmartCubeSelected, sectorSelected, categorySelected, costStructureRegionSelected]);

  // TO KEEP
  useEffect(() => {
    if (isUserDataFetched && smartCubeCostStructureBreakdownData?.type === "SUCCESS_SMARTCUBE_COST_STRUCTURE_BREAKDOWN_ACTION") {
      setCostStructureBreakdownData(smartCubeCostStructureBreakdownData?.data || []);
      setCostStructureBreakdownLastUpdated(smartCubeCostStructureBreakdownData?.lastUpdated || null);
      setCostStructureBreakdownNextUpdate(smartCubeCostStructureBreakdownData?.nextUpdate || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartCubeCostStructureBreakdownData, isUserDataFetched]);

  useEffect(() => {
  return () => {
    dispatch({ type: "RESET_SMARTCUBE_PAGE_FILTER_ACTION", payload: "RESET_SMARTCUBE_PAGE_FILTER_ACTION" });
    dispatch({ type: "RESET_SMARTCUBE_CHART_FILTERS_ACTION", payload: "RESET_SMARTCUBE_CHART_FILTERS_ACTION" });
    dispatch({ type: "RESET_SMARTCUBE_MARKET_SIZE_FORECAST_ACTION", payload: "RESET_SMARTCUBE_MARKET_SIZE_FORECAST_ACTION" });
    dispatch({ type: "RESET_SMARTCUBE_CATEGORY_RISK_ACTION", payload: "RESET_SMARTCUBE_CATEGORY_RISK_ACTION" });
    dispatch({ type: "RESET_SMARTCUBE_TEXT_WIDGETS_ACTION", payload: "RESET_SMARTCUBE_TEXT_WIDGETS_ACTION" });
    dispatch({ type: "RESET_SMARTCUBE_PRICE_TREND_ACTION", payload: "RESET_SMARTCUBE_PRICE_TREND_ACTION" });
    dispatch({ type: "RESET_SMARTCUBE_COST_DRIVERS_ACTION", payload: "RESET_SMARTCUBE_COST_DRIVERS_ACTION" });
    dispatch({ type: "RESET_SMARTCUBE_COST_STRUCTURE_BREAKDOWN_ACTION", payload: "RESET_SMARTCUBE_COST_STRUCTURE_BREAKDOWN_ACTION" });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetButtonHandler = () => {
    if (l1Category && l2Category) {
      dispatch(FetchSmartCubePageFilterAction(l1Category, l2Category));
    }
  };
  
  /* istanbul ignore next */
  const checkPDFDownloaded = (isPdfDownloaded, isModal, fileURL, isExcel, isTable, isJpeg) => {
    if (isPdfDownloaded) {
      if (isModal) {
        showToast("success", !isJpeg ? "PDF of full page successfully downloaded." : "JPEG of full page successfully downloaded.", {
          icon: <img src={Vector} alt="image" style={{ width: "20px", height: "20px" }} />
        });
      } else if (isTable) {
        showToast("success", !isJpeg ? "PDF of a table successfully downloaded." : "JPEG of a table successfully downloaded.", {
          icon: <img src={Vector} alt="image" style={{ width: "20px", height: "20px" }} />
        });
      } else {
        showToast("success", !isJpeg ? "PDF of a chart successfully downloaded." : "JPEG of a chart successfully downloaded.", {
          icon: <img src={Vector} alt="image" style={{ width: "20px", height: "20px" }} />
        });
      }
    } else {
      showToast("Error", "Failed to download. Please try again.", {
        icon: <img src={Vector} alt="image" style={{ width: "20px", height: "20px" }} />
      });
    }
  };

  const value = {
    isUserDataLoading, 
    smartCubeFilterData, smartCubeChartFiltersData,
    isSmartCubeFilterDataLoading,
    sectorList, sectorSelected, onSectorChange,
    categoryList, categorySelected, onCategoryChange,
    smartCubeFromDates, smartCubeFromDateSelected, onSmartCubeFromDateChange,
    smartCubeToDates, smartCubeToDateSelected, onSmartCubeToDateChange,
    renderSmartCubeFilterDropdown,
    resetButtonHandler,
    isSmartCubeChartFiltersLoading,
    countryList, countrySelected, onCountryChange,
    commodityList, commoditySelected, onCommodityChange,
    costDriverList, costDriverSelected, onCostDriverChange,
    costStructureRegionList, costStructureRegionSelected, onCostStructureRegionChange,
    isSmartCubePriceTrendLoading,
    priceTrendChartData, priceTrendUnit, priceTrendLastUpdated, priceTrendNextUpdate,
    isSmartCubeCostDriversLoading,
    costDriversChartData, costDriversLastUpdated, costDriversNextUpdate,
    isSmartCubeCostStructureBreakdownLoading,
    costStructureBreakdownData, costStructureBreakdownLastUpdated, costStructureBreakdownNextUpdate,
    isSmartCubeMarketSizeForecastLoading,
    marketSizeForecastChartData, marketSizeForecastUnit, marketSizeForecastCagr,
    marketSizeForecastLastUpdated, marketSizeForecastNextUpdate,
    isSmartCubeCategoryRiskLoading,
    categoryRiskChartData, categoryRiskAvg, categoryRiskLastUpdated, categoryRiskNextUpdate,
    isSmartCubeTextWidgetsLoading,
    opportunitiesData, opportunitiesLastUpdated, opportunitiesNextUpdate,
    risksData, risksLastUpdated, risksNextUpdate,
    megaTrendsData, megaTrendsLastUpdated, megaTrendsNextUpdate,
    sustainabilityTrendsData, sustainabilityTrendsLastUpdated, sustainabilityTrendsNextUpdate,
    linechartRef, donutchartRef, costDriversRef, marketSizeRef, categoryRiskRef,
    checkPDFDownloaded,
    showSmartCubeDetailsModal, setShowSmartCubeDetailsModal
  };

  return <SmartCubeSourceContext.Provider value={value}>{children}</SmartCubeSourceContext.Provider>;
};
